import { useState } from "react";
import { Cohort } from "../../model/cohort";
import { Stream } from "../../model/stream";
import { useToast } from "../../../../context/toast/toast-provider";
import { addStream, deleteStream, updateStream } from "../../service/stream-service";
import BaseForm from "../../../../component/form/base-form";
import { FieldDefinition } from "../../../../component/form/field-definition";

const streamFieldDefinitions: FieldDefinition[] = [
    {
        id: "name",
        name: "Name",
        type: "text",
        required: true,
        disabled: false,
        min: 2,
        max: 63
    },
    {
        id: "active",
        name: "Active",
        type: "checkbox",
        required: false,
        disabled: false,
    }
];

export default function CohortStreamHandler({
    cohort,
    stream,
    onSaved,
    onDeleted,
    onCancel,
}: {
    cohort: Cohort,
    stream: Stream | undefined,
    onSaved: () => void,
    onDeleted: () => void,
    onCancel: () => void,
}) {
    const newModel = stream === undefined;
    const [model, setModel] = useState<Stream>(stream ?? {
        name: "",
        cohortId: cohort.id!,
        active: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const toast = useToast();

    const handleSubmit = (stream: Stream) => {
        setSubmitting(true);
        const payload = { ...stream, cohortId: cohort.id! };

        if (newModel) {
            addStream(cohort.id!, payload).then(() => {
                toast.addToast(`Stream "${stream.name}" created`, "success");
                setSubmitting(false);
                onSaved();
            }).catch(() => {
                toast.addToast(`Unable to create stream`, "error");
                setSubmitting(false);
            });
        } else {
            updateStream(cohort.id!, payload).then(() => {
                toast.addToast(`Stream "${stream.name}" updated`, "success");
                setSubmitting(false);
                onSaved();
            }).catch(() => {
                toast.addToast(`Unable to update stream`, "error");
                setSubmitting(false);
            });
        }
    }

    const handleDelete = () => {
        setDeleting(true);
        deleteStream(cohort.id!, stream!.id!).then(() => {
            toast.addToast(`Stream "${stream!.name}" deleted`, "success");
            setDeleting(false);
            onDeleted();
        }).catch(() => {
            toast.addToast(`Unable to delete stream`, "error");
            setDeleting(false);
        });
    }

    return (
        <BaseForm
            name={newModel ? "Add Stream" : `Edit Stream: ${stream!.name}`}
            model={model}
            setModel={setModel}
            fields={streamFieldDefinitions}
            submitButtonText={newModel ? "Create" : "Update"}
            submitEntity={handleSubmit}
            submitting={submitting}
            deleteButtonText={newModel ? undefined : "Delete"}
            deleteConfirmationText={`Are you sure you want to delete stream "${stream?.name}"?`}
            deleteEntity={newModel ? undefined : handleDelete}
            deleting={deleting}
            cancelButtonText="Back"
            cancel={onCancel}
        />
    );
}
